import { EventModeOutcome, EventModes, eventModesFromSave, eventModesSection } from "@highmarch/app";
import type { SaveRecord } from "@highmarch/app";
import { Grid, LevelModifiers } from "@highmarch/domain";
import { eventModePanelHtml } from "../ui/hub/eventModePanels";
import { eventEndHtml } from "../ui/modes/eventRunEnd";
import { WarMap } from "../ui/warMap";
import { portraitUrl } from "../ui/portraits";
import { toast } from "../ui/toast";
import { eventClock } from "./events";
import { EndScreenFacts, Feature } from "./registry";

/** The panel sits under the live event's track on the Events sheet. */
const PANEL_ORDER = 10;
/** Its end-screen block sits where Daily Road's does (before the events' block, END_ORDER 20 there). */
const END_ORDER = 15;
/** Run outcomes kept for end-screen redraws. */
const OUTCOMES_KEPT = 4;
/** Race Week's road sketch in its panel (CSS px; ui/styles/eventModes.css .mroad). */
const ROAD_W = 108, ROAD_H = 80;

/** Whether the save still holds a suspended Odyssey map (to CONTINUE); without one, a begun map was abandoned. */
const odysseySuspended = (save: SaveRecord): boolean => save.suspended?.record.modifiers?.event?.mode === "odyssey";

/**
 * The weekly event modes (MP-21, docs/MODES.md §5): Boss Week, Race Week and the Odyssey on the Events sheet (their
 * panel under the week's track, its gold button starting the run), the end screen's prize block and next run, the
 * `eventModes` save section bound to the save, Guide topics for each, and debug hooks for captures. Rules live in
 * @highmarch/app eventModes.ts; the run is a normal one built by the level flow (VIP and gear count: owner D1).
 */
export const eventModesFeature: Feature = {
  id: "eventModes",
  register(ctx, registry) {
    const modes = new EventModes(ctx.catalog, eventModesFromSave(ctx.save));
    eventModesSection.bind(ctx.save, () => modes.state.toJson());
    const now = eventClock(() => ctx.now(), ctx.debug, typeof location === "undefined" ? "" : location.search);
    // A begun Odyssey map with no suspended copy left was abandoned (from the battle card): that attempt is over.
    const reconcile = (): void => { if (modes.state.odysseyInProgress && !odysseySuspended(ctx.save)) { modes.abandoned(); ctx.persist(); } };

    registry.eventPanel({
      id: "eventMode", order: PANEL_ORDER,
      html: (e) => { reconcile(); return eventModePanelHtml(ctx.balance, modes, e, now()); },
      bind: (panel, api) => {
        const start = (run: { level: number; modifiers: LevelModifiers } | null): void => {
          if (!run) { toast(ctx.ui, "Nothing to play right now"); return; }
          ctx.persist();
          api.close();
          registry.startRun(run.level, run.modifiers);
        };
        for (const b of panel.querySelectorAll<HTMLButtonElement>(".bossgo")) b.onclick = () => start(modes.bossRun(now(), Number(b.dataset.tier)));
        panel.querySelector<HTMLButtonElement>(".racego")?.addEventListener("click", () => start(modes.raceRun(now())));
        panel.querySelector<HTMLButtonElement>(".odygo")?.addEventListener("click", () => start(modes.odysseyRun(now())));
        // Race Week's road: the level's ground with the kind of road it takes, inked as the level-load war map does
        const road = panel.querySelector<HTMLElement>(".mroad"), race = modes.race(now());
        if (road && race) {
          const map = new WarMap({ grid: Grid.fromLevel(ctx.balance.level(race.road.level)), seed: race.road.level, towerIcons: [], fortressIcon: portraitUrl("base", 2), calm: true });
          map.fit(ROAD_W, ROAD_H);
          map.step(0, 1, true);
          road.append(map.canvas);
        }
      },
    });

    // A fresh run's start: an Odyssey map begins its attempt (map 1 opens one; a map begun again was abandoned).
    registry.runStarted((_level, m) => { if (m.event) { modes.beginRun(m); ctx.persist(); } });
    registry.nextEventRun = (m, won) => modes.nextRun(m, won, now());

    // The end screen: a run's outcome is settled once (its prizes, its time) and drawn from the cache.
    const outcomes = new Map<string, EventModeOutcome | null>();
    const outcomeFor = (f: EndScreenFacts): EventModeOutcome | null => {
      const key = `ev:${f.sim.seed}:${f.won ? "w" : "l"}${f.sim.stagesCleared}:${f.sim.tick}`;
      if (!outcomes.has(key)) {
        const o = modes.finishRun({ modifiers: f.sim.modifiers, level: f.level, won: f.won, ms: f.sim.clearMs, baseHp: f.sim.baseHp, key, now: now() });
        outcomes.set(key, o);
        if (outcomes.size > OUTCOMES_KEPT) outcomes.delete(outcomes.keys().next().value!);
        ctx.persist();
      }
      return outcomes.get(key) ?? null;
    };
    registry.endScreenExtra({ id: "eventModes", order: END_ORDER, html: (f) => { if (!f.sim.isEventRun) return ""; const o = outcomeFor(f); return o ? eventEndHtml(o) : ""; } });

    const from = ctx.balance.events.unlockAfterLevel + 1;
    registry.guideTopic({
      id: "bossWeek", group: "castle", label: "Boss Week", art: { icon: "skull" }, fromLevel: from, steps: [],
      lines: [
        "**Boss Week** is one boss fight in six tiers. Beat a tier to open the next; each pays its prize once.",
        "**Elite** waits after tier 5, with this week's Trial twist on top. Gear and VIP all count.",
      ],
    });
    registry.guideTopic({
      id: "raceWeek", group: "castle", label: "Race Week", art: { icon: "fast" }, fromLevel: from, steps: [],
      lines: [
        "**Race Week** is one road with the same cards every try. Clear it fast for **bronze**, **silver** and **gold**.",
        "The clock stops while you choose a card, so think. Call waves early to cut your time; a gold pays every medal still owed.",
      ],
    });
    registry.guideTopic({
      id: "odyssey", group: "castle", label: "Odyssey", art: { icon: "map" }, fromLevel: from, steps: [],
      lines: [
        "An **Odyssey** is three maps in a row on one fortress: the lives you keep carry to the next map.",
        "Five towers make the week's deck. Lose a map and that attempt ends; you get three attempts a week.",
      ],
    });

    if (ctx.debug && typeof window !== "undefined") {
      // Captures: the week's mode, a run's start, a faked result (best time, tiers cleared, an attempt's progress).
      (window as unknown as { __tdEvModes: unknown }).__tdEvModes = {
        modes, week: () => modes.week(now()),
        clearTiers: (n: number) => { const w = modes.week(now()); for (let t = 1; t <= n && w; t++) modes.state.recordBoss(w.monday, t, true, `debug:boss:${t}:${Date.now()}`); ctx.persist(); },
        best: (ms: number) => { const w = modes.week(now()); if (w) modes.finishRun({ modifiers: modes.raceRun(now())!.modifiers, level: 1, won: true, ms, baseHp: 1, key: `debug:race:${Date.now()}`, now: now() }); ctx.persist(); },
        odyssey: (map: number, lives: number) => { const w = modes.week(now()); if (!w) return; for (let m = 1; m < map; m++) { modes.state.beginOdyssey(w.monday, m, 99); modes.state.recordOdyssey(w.monday, m, true, lives, 3, `debug:ody:${m}:${Date.now()}`); } ctx.persist(); },
      };
    }
  },
};
